import {
  PlanInfo,
  AllowedModelMode,
  DEPLOYMENT_TRACKS
} from '../types/plans';
import { getPlan, PLAN_RANKS } from '../config/planConfig';

/**
 * Checks if the plan is Pay As You Go.
 */
export function isPaygPlan(planInfo: PlanInfo): boolean {
  return planInfo.plan === 'payg';
}

/**
 * Gets the display name for a plan combination.
 * Label text comes from planConfig.ts — update it there to rename a plan app-wide.
 */
export function getPlanDisplayName(planInfo: PlanInfo): string {
  const track = DEPLOYMENT_TRACKS[planInfo.track];
  const planName = getBillingPlanDisplayName(planInfo.plan);

  if (planInfo.track === 'cloud') {
    return planName;
  } else if (track) {
    return `${track.name} (${planName})`;
  } else {
    return planName;
  }
}

/**
 * Checks if a plan is the free tier.
 */
export function isFreePlan(planInfo: PlanInfo): boolean {
  return planInfo.plan === 'free';
}

/**
 * Checks if a plan is Basic (or legacy Plus) or above.
 */
export function isPlusOrAbove(planInfo: PlanInfo): boolean {
  return (PLAN_RANKS[planInfo.plan] ?? 0) >= (PLAN_RANKS['basic'] ?? 0);
}

/**
 * Checks if a plan is Standard (or legacy Ultra) or above ($36/mo).
 */
export function isUltraOrAbove(planInfo: PlanInfo): boolean {
  return (PLAN_RANKS[planInfo.plan] ?? 0) >= (PLAN_RANKS['standard'] ?? 0);
}

/**
 * Checks if a plan is Premium (or legacy Pro, top tier, $100/mo).
 */
export function isProPlan(planInfo: PlanInfo): boolean {
  return (PLAN_RANKS[planInfo.plan] ?? 0) >= (PLAN_RANKS['premium'] ?? 0);
}

// ─── Model access ─────────────────────────────────────────────────────────────

/**
 * Returns whether the plan is allowed to use the given model mode.
 * Every model tier (Everyday/Smartest/Fastest/Uncensored) is
 * available to every plan, including Free — there is no model-based gating.
 */
export function canUseModel(_planInfo: PlanInfo, _mode: AllowedModelMode): boolean {
  return true;
}

/**
 * Plans where the Fastest tier has unlimited usage, excluded from AI budget
 * metering entirely. Deliberately an exact-match list (not "Basic or above")
 * rather than reusing isPlusOrAbove/PLAN_RANKS: Free has no included budget
 * to waive against and PAYG is metered per-use by design, and legacy
 * rank-based tiers (plus/ultra/pro) aren't guaranteed to be normalized to
 * these canonical ids server-side. Mirrors the backend's source of truth,
 * org_member_billing_service.is_unlimited_included_model() in
 * backend/services/org_member_billing_service.py; keep both in sync.
 */
const UNLIMITED_FASTEST_MODEL_PLANS: ReadonlySet<string> = new Set(['basic', 'standard', 'premium']);

export function isFastestModelUnlimited(plan: string | null | undefined): boolean {
  return !!plan && UNLIMITED_FASTEST_MODEL_PLANS.has(plan);
}

// ─── Feature flags ────────────────────────────────────────────────────────────

/**
 * Whether the plan may connect integrations (aligned with shared/plans.json
 * features_gated.allowIntegrations: false on free; true on Basic+ and PAYG).
 */
export function canUseIntegrations(planInfo: PlanInfo): boolean {
  return isPlusOrAbove(planInfo);
}

/**
 * Whether the plan can generate images.
 * Free plan cannot. Basic/Standard/Premium have a monthly quota. PAYG is billed per generation.
 */
export function canGenerateImages(planInfo: PlanInfo): boolean {
  return isPlusOrAbove(planInfo) || isPaygPlan(planInfo);
}

// ─── Limits ───────────────────────────────────────────────────────────────────

/**
 * Maximum number of projects (spaces) the plan allows.
 * Returns -1 for unlimited.
 */
export function getProjectLimit(planInfo: PlanInfo): number {
  return getPlan(planInfo.plan).limits.projects;
}

/**
 * Monthly image generation limit.
 * Returns -1 for unlimited; 0 means no access.
 */
export function getMonthlyImageGenLimit(planInfo: PlanInfo): number {
  return getPlan(planInfo.plan).limits.imageGenerations;
}

// ─── Upgrade helpers ──────────────────────────────────────────────────────────

export function isFounderPlan(planInfo: PlanInfo): boolean {
  return planInfo.plan.startsWith('founder-');
}

export function getNextUpgradePlan(currentPlan: PlanInfo): PlanInfo | null {
  switch (currentPlan.plan) {
    case 'free':
      return { ...currentPlan, plan: 'basic' };
    case 'plus':   // legacy
    case 'basic':
      return { ...currentPlan, plan: 'standard' };
    case 'ultra':  // legacy
    case 'standard':
      return { ...currentPlan, plan: 'premium' };
    case 'pro':    // legacy
    case 'premium':
    case 'enterprise':
      return null;
    default:
      if (isFounderPlan(currentPlan)) return null;
      return null;
  }
}

export function getUpgradeMessage(currentPlan: PlanInfo): string {
  const nextPlan = getNextUpgradePlan(currentPlan);
  if (!nextPlan) {
    if (isFounderPlan(currentPlan)) {
      return 'You\'re on a founder plan with unlimited access!';
    }
    return 'You\'re already on our highest plan!';
  }

  const planName = getBillingPlanDisplayName(nextPlan.plan);
  return `Upgrade to ${planName} to unlock more features and higher limits.`;
}

const LEGACY_DISPLAY_NAMES: Record<string, string> = {
  plus: 'Plus',
  ultra: 'Ultra',
  pro: 'Pro',
};

/**
 * Converts a raw billing plan key (as stored in the DB) to its user-facing display name.
 * Legacy subscribers keep their original plan names (Plus, Ultra, Pro).
 * Label text comes from planConfig.ts — update it there to rename a plan app-wide.
 */
export function getBillingPlanDisplayName(plan: string): string {
  if (plan in LEGACY_DISPLAY_NAMES) return LEGACY_DISPLAY_NAMES[plan];
  if (['free', 'basic', 'standard', 'premium', 'payg'].includes(plan)) {
    return getPlan(plan).displayName;
  }
  const extras: Record<string, string> = {
    business: 'Business',
    enterprise: 'Enterprise',
    payment_required: 'Payment Required',
  };
  return extras[plan] ?? plan;
}

// Legacy compatibility
export const isFreemiumPlan = isFreePlan;

export interface LimitExceededData {
  error: 'LIMIT_EXCEEDED';
  upgrade_required: true;
  plan_type: string;
  limit_type: string;
  limits: Record<string, { used: number; max: number }>;
  message: string;
}

/**
 * Extracts a structured LIMIT_EXCEEDED payload from a fetch error, handling two cases:
 *  1. Direct 403: FastAPI wraps the HTTPException detail in `{"detail": {...}}`, so the
 *     payload lives at `error.data.detail` (not `error.data` directly).
 *  2. 500 wrapping a 403: the backend occasionally re-raises HTTPException as 500;
 *     in that case `error.data.detail` is a Python-formatted dict string like
 *     `"403: {'error': 'LIMIT_EXCEEDED', 'upgrade_required': True, ...}"`
 */
export function extractLimitExceededError(error: any): LimitExceededData | null {
  if (!error) return null;

  // Direct 403 — FastAPI returns {"detail": {...}} so unwrap one level
  if (error.status === 403) {
    // `detail` may be the nested object, or the data itself if the route returns it directly
    const payload = error.data?.detail ?? error.data;
    if (payload?.error === 'LIMIT_EXCEEDED') {
      return payload as LimitExceededData;
    }
  }

  // 500 wrapping a 403 — `error.data.detail` is a Python dict string
  if (error.status === 500) {
    const detail: string | undefined =
      typeof error.data?.detail === 'string' ? error.data.detail :
      typeof error.message === 'string' ? error.message : undefined;

    if (detail && detail.includes("'error': 'LIMIT_EXCEEDED'")) {
      try {
        // Convert Python dict to valid JSON
        const raw = detail.replace(/^.*?403:\s*/, '');
        const json = raw
          .replace(/'/g, '"')
          .replace(/\bTrue\b/g, 'true')
          .replace(/\bFalse\b/g, 'false')
          .replace(/\bNone\b/g, 'null');
        const parsed = JSON.parse(json);
        if (parsed?.error === 'LIMIT_EXCEEDED') return parsed as LimitExceededData;
      } catch {
        // Regex fallback when the string can't be cleanly converted to JSON
        const planType = detail.match(/'plan_type':\s*'(\w+)'/)?.[1] ?? 'free';
        const limitType = detail.match(/'limit_type':\s*'(\w+)'/)?.[1] ?? 'monthly_cost';
        const message = detail.match(/'message':\s*"([^"]+)"/)?.[1] ?? '';
        return {
          error: 'LIMIT_EXCEEDED',
          upgrade_required: true,
          plan_type: planType,
          limit_type: limitType,
          limits: {},
          message,
        };
      }
    }
  }

  return null;
}
